import React from 'react';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { Briefcase, CalendarDays, ClipboardList } from 'lucide-react';

export interface IStatCardDef {
  key: string;
  label: string;
  value: number;
  icon: LucideIcon;
  gradient: string;
  iconBg: string;
  valueColor: string;
}

export interface IStatCardsProps {
  loading: boolean;
  activeApplications: number;
  pendingAssessments: number;
  upcomingInterviews: number;
}

export const StatCards = ({
  loading,
  activeApplications,
  pendingAssessments,
  upcomingInterviews,
}: IStatCardsProps) => {
  const cards: IStatCardDef[] = [
    {
      key: 'applications',
      label: 'Active Applications',
      value: activeApplications,
      icon: Briefcase,
      gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%)',
      iconBg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      valueColor: '#059669',
    },
    {
      key: 'assessments',
      label: 'Pending Assessments',
      value: pendingAssessments,
      icon: ClipboardList,
      gradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08) 0%, rgba(234, 88, 12, 0.04) 100%)',
      iconBg: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
      valueColor: '#ea580c',
    },
    {
      key: 'interviews',
      label: 'Upcoming Interviews',
      value: upcomingInterviews,
      icon: CalendarDays,
      gradient: 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(126, 34, 206, 0.04) 100%)',
      iconBg: 'linear-gradient(135deg, #a855f7 0%, #7e22ce 100%)',
      valueColor: '#7e22ce',
    },
  ];

  return (
    <Box
      component="section"
      aria-label="Progress summary"
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
        },
        gap: 3,
      }}
    >
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <Card
            key={card.key}
            sx={{
              background: card.gradient,
              border: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
              p: 2,
            }}
          >
            <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 1, '&:last-child': { pb: 1 } }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: card.iconBg,
                  color: '#fff',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
                  mb: 1.5,
                  transition: 'transform 200ms ease',
                  '&:hover': {
                    transform: 'scale(1.08)',
                  },
                }}
              >
                <Icon size={24} />
              </Box>

              {loading ? (
                <Skeleton
                  variant="text"
                  width={60}
                  height={40}
                  sx={{ mb: 0.5 }}
                />
              ) : (
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    color: card.valueColor,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {card.value}
                </Typography>
              )}

              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                {card.label}
              </Typography>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};
